<script lang="ts" setup>
  import { toRaw } from 'vue'
  import { toast } from 'vue-sonner'
  import { useServices } from '~/api/models'
  import { useServiceWorker } from '~/composables/useServiceWorker'

  const services = useServices()
  const mapStore = useMapStore()
  const authStore = useAuthStore()
  const assetStore = useAssetStore()
  const filterStore = useFilterStore()

  const worker = useServiceWorker()
  const { t } = useI18n()

  const { activeLicence, lastActiveLicence } = storeToRefs(authStore)
  const { data: assetTypes } = services.assets.useGetAssetTypesQuery()
  const { data: mapIconColors } = services.assets.useGetMapIconColorsQuery()
  const { data: alertLevelColors } = services.assets.useGetAlertLevelColorsQuery()
  const { data: routes } = services.routes.useGetAllQuery()
  const { data: permissionsData, error: permissionsError } = services.permissions.useGetAllQuery()

  const isInitialLoad = ref(true)

  watch(assetTypes, (value) => {
    if (!value) return
    filterStore.setAvailableAssetTypes(value)
    mapStore.setAssetTypesLoaded(value)

    if (!activeLicence.value) return
    const switchedBetweenLicences = !isInitialLoad.value && lastActiveLicence.value?.id !== activeLicence.value?.id
    if (switchedBetweenLicences) {
      const allAssetTypeIds = value.map((assetType) => assetType.assetTypeId)
      filterStore.setAssetTypeIds(allAssetTypeIds)
    }

    isInitialLoad.value = false
  })

  watch(routes, (value) => {
    if (!value) return

    // Convert reactive proxy to plain object for worker serialization
    const plainRoutes = toRaw(value)

    worker.sendMessage({
      type: 'createSectionsRouteMap',
      payload: {
        routes: plainRoutes
      }
    })
  })

  worker.on('sectionsRouteMapCreated', (payload) => {
    assetStore.setSectionRouteMap(payload as Map<number, number>)
  })

  watch(
    mapIconColors,
    (value) => {
      if (!value) return
      mapStore.setMapColorConfig(value)
    },
    { immediate: true }
  )

  watch(
    alertLevelColors,
    (value) => {
      if (!value) return
      mapStore.setAlertLevelColorsLoaded(value)
    },
    { immediate: true }
  )

  watch(
    [permissionsData, permissionsError],
    ([data, error]) => {
      if (error) {
        console.error('[DataLoader] Failed to load permissions:', error)
        authStore.clearPermissions()
        authStore.clearActiveLicence()

        toast.error(t('permissions.loadError.title'), {
          description: t('permissions.loadError.description')
        })
      } else if (data) {
        authStore.setPermissions(data)
      }
    },
    { immediate: true }
  )
</script>

<template>
  <slot />
</template>
